import { moveItemInArray } from '@angular/cdk/drag-drop';
import { createReducer, on } from '@ngrx/store';

import { DashboardColumn, DashboardInstallationData, DeviceRoles, SortOrder } from '@vi-iot/client';

import {
  ColumnCreationFlow,
  DashboardInstallationColumn,
  KanbanBoardState,
} from './kanban-board.state';
import { KanbanBoardActions } from './kanban-board.action';
import { DashboardFilter } from './kanban-board.model';
import { isEqual, uniqWith } from 'lodash-es';

export const kanbanBoardInitState: KanbanBoardState = {
  columnDialogVisible: false,
  columnDialogData: null,
  columns: null,
  fetchingDashboardStatus: {
    loading: false,
    loaded: false,
    error: null,
  },
  predefinedFilters: [],
  filters: [],
  selectedFilters: [],
  quickViewDashboardData: {
    equipmentSummary: null,
    installation: null,
    smartNotes: null,
    quickViewDeviceIndex: null,
  },
  forcedStoreUpdateStatus: {
    loading: false,
    loaded: false,
    error: false,
  },
  recoverDataInCaseDeleteError: null,
  columnCreationFlow: null,
  idOfOriginalColumnDuringColumnCreationAndMoveProcess: null,
  installationList: {
    error: null,
    loading: false,
    value: null,
    buttonFilterData: null,
    canFetch: false,
    cursor: null,
  },
};

export const kanbanBoardReducer = createReducer(
  kanbanBoardInitState,
  on(
    KanbanBoardActions.getDashboard.attempt,
    KanbanBoardActions.recreateSnapshot.attempt,
    (state): KanbanBoardState => ({
      ...state,
      fetchingDashboardStatus: { loading: true, loaded: false, error: null },
    })
  ),
  on(
    KanbanBoardActions.columnDialogActions.close,
    KanbanBoardActions.openForcedDashboardRefreshDialog,
    (state): KanbanBoardState => ({
      ...state,
      columnDialogVisible: false,
      columnDialogData: null,
    })
  ),
  on(
    KanbanBoardActions.columnDialogActions.open,
    (state, { column }): KanbanBoardState => ({
      ...state,
      columnDialogVisible: true,
      columnDialogData: column,
    })
  ),

  on(
    KanbanBoardActions.getDashboard.success,
    (state, { columns }): KanbanBoardState => {
      const sortedColumns: DashboardInstallationColumn[] = sortColumnsByNeighbor(
        structuredClone(columns)
      )
        .map(addAdditionalFlagsToColumn)
        .map((column) => ({
          ...column,
          installations: removeEmptyDevices(column.installations),
        }));

      return {
        ...state,
        fetchingDashboardStatus: { loading: false, loaded: true, error: null },
        columns: sortedColumns,
        installationList: {
          ...state.installationList,
        },
      };
    }
  ),

  on(
    KanbanBoardActions.getDashboard.error,
    KanbanBoardActions.getDashboard.error,
    (state, { error }): KanbanBoardState => ({
      ...state,
      fetchingDashboardStatus: { loading: false, loaded: false, error },
    })
  ),

  on(
    KanbanBoardActions.addTempColumn,
    (state): KanbanBoardState => {
      const lastColumn = state.columns[state.columns.length - 1];
      const tempColumnToAdd: DashboardInstallationColumn = {
        id: lastColumn.id + 1,
        name: '',
        selfDefined: true,
        sortOrder: SortOrder.asc,
        installations: [],
        leftNeighbor: lastColumn.id,
        version: '',
        loading: false,
        canFetch: false,
        isTemporary: true,
        inputShowed: true,
        allInstallationIds: [],
        installationIds: [],
      };

      return {
        ...state,
        columns: [...state.columns, tempColumnToAdd],
        columnCreationFlow: ColumnCreationFlow.Add,
      };
    }
  ),

  on(
    KanbanBoardActions.deleteTempColumn,
    (state): KanbanBoardState => {
      return {
        ...state,
        columns: state.columns.filter((column) => !column.isTemporary),
        columnCreationFlow: null,
      };
    }
  ),

  on(
    KanbanBoardActions.manageColumnNameInput,
    (state, { columnId, inputShowed }): KanbanBoardState => {
      const columns = structuredClone(state.columns);
      columns.find((column) => column.id === columnId).inputShowed = inputShowed;

      return {
        ...state,
        columns,
      };
    }
  ),

  on(
    KanbanBoardActions.addColumn.success,
    (state, { column }): KanbanBoardState => {
      const columnToAdd: DashboardInstallationColumn = addAdditionalFlagsToColumn(column);

      return {
        ...state,
        columns: [...state.columns, columnToAdd],
      };
    }
  ),

  on(
    KanbanBoardActions.deleteColumn.success,
    (state, { id }): KanbanBoardState => ({
      ...state,
      columns: state.columns.filter((column) => column.id !== id),
    })
  ),

  on(
    KanbanBoardActions.editColumnName.success,
    (state, { editColumnPayload, id }): KanbanBoardState => {
      const columnBeforeModification = state.columns.find((item) => item.id === id);

      const modifiedColumn: DashboardInstallationColumn = {
        ...columnBeforeModification,
        name: editColumnPayload.name,
        leftNeighbor: editColumnPayload.leftNeighbor,
        version: editColumnPayload.version,
        inputShowed: false,
      };

      return {
        ...state,
        columns: state.columns.map((el) => (el.id === modifiedColumn.id ? modifiedColumn : el)),
      };
    }
  ),

  on(
    KanbanBoardActions.editColumnName.error,
    (state, { id }): KanbanBoardState => {
      const columns = structuredClone(state.columns);
      columns.find((column) => column.id === id).inputShowed = false;

      return { ...state, columns };
    }
  ),

  on(
    KanbanBoardActions.editColumnName.retryAborted,
    (state, { columnId }): KanbanBoardState => {
      const columns = state.columns.map((column) => {
        if (column.id !== columnId) {
          return column;
        } else {
          return {
            ...column,
            inputShowed: false,
          };
        }
      });

      return {
        ...state,
        columns,
      };
    }
  ),

  on(
    KanbanBoardActions.setColumnSortOrder.success,
    (state, { columnId, sortOrder, installations }): KanbanBoardState => {
      const columns = structuredClone(state.columns);
      const columnToEdit = columns.find((column) => column.id === columnId);

      columnToEdit.sortOrder = sortOrder;
      columnToEdit.installations = installations;
      columnToEdit.canFetch = installations.length < columnToEdit.allInstallationIds.length;

      return { ...state, columns };
    }
  ),

  on(
    KanbanBoardActions.predefineDashboardFilter.set,
    (state, { filters }): KanbanBoardState => ({
      ...state,
      predefinedFilters: filters,
    })
  ),

  on(
    KanbanBoardActions.predefineDashboardFilter.clear,
    (state): KanbanBoardState => ({
      ...state,
      predefinedFilters: [],
    })
  ),

  on(
    KanbanBoardActions.getFilters.success,
    (state, { filters }): KanbanBoardState => ({
      ...state,
      filters,
    })
  ),

  on(
    KanbanBoardActions.setDashboardFilter.success,
    (state, { filters }): KanbanBoardState => ({
      ...state,
      selectedFilters: filters,
    })
  ),

  on(
    KanbanBoardActions.fetchKanbanDashboardWithFilters.success,
    (state, { columns }): KanbanBoardState => {
      const sortedColumns: DashboardInstallationColumn[] = sortColumnsByNeighbor(
        structuredClone(columns)
      )
        .map((col) => addAdditionalFlagsToColumn(col))
        .map((sortedColumn) => {
          const cachedInstallationIds = state.columns.find(
            (stateColumn) => stateColumn.id === sortedColumn.id
          ).allInstallationIds;

          return {
            ...sortedColumn,
            allInstallationIds: cachedInstallationIds,
            installations: removeEmptyDevices(sortedColumn.installations),
          };
        });

      return {
        ...state,
        fetchingDashboardStatus: { loading: false, loaded: true, error: null },
        columns: sortedColumns,
      };
    }
  ),

  on(
    KanbanBoardActions.fetchKanbanDashboardWithFilters.error,
    (state, { error }): KanbanBoardState => {
      return {
        ...state,
        fetchingDashboardStatus: { loading: false, loaded: false, error },
        columns: null,
      };
    }
  ),

  on(
    KanbanBoardActions.openQuickViewPanel,
    (state, { installation, deviceIndex }): KanbanBoardState => ({
      ...state,
      quickViewDashboardData: {
        ...state.quickViewDashboardData,
        installation,
        quickViewDeviceIndex: deviceIndex,
      },
    })
  ),

  on(
    KanbanBoardActions.closeQuickViewPanel,
    (state): KanbanBoardState => ({
      ...state,
      quickViewDashboardData: {
        equipmentSummary: null,
        installation: null,
        quickViewDeviceIndex: null,
        smartNotes: null,
      },
    })
  ),

  on(
    KanbanBoardActions.getInstallationSmartNotes.success,
    (state, { activeErrors }): KanbanBoardState => {
      const smartNotes = activeErrors.map((error) => ({ ...error, loading: null }));

      return {
        ...state,
        quickViewDashboardData: {
          ...state.quickViewDashboardData,
          smartNotes,
        },
      };
    }
  ),

  on(
    KanbanBoardActions.getInstallationSmartNotes.error,
    (state): KanbanBoardState => {
      return {
        ...state,
        quickViewDashboardData: {
          ...state.quickViewDashboardData,
          smartNotes: [],
        },
      };
    }
  ),

  on(
    KanbanBoardActions.getQuickViewErrorDetails.attempt,
    (state, { smartNoteId }): KanbanBoardState => {
      const smartNotes = state.quickViewDashboardData.smartNotes.map((smartNote) => {
        if (smartNoteId === smartNote.id) {
          return {
            ...smartNote,
            loading: true,
          };
        }

        return smartNote;
      });

      return {
        ...state,
        quickViewDashboardData: {
          ...state.quickViewDashboardData,
          smartNotes,
        },
      };
    }
  ),

  on(
    KanbanBoardActions.getQuickViewErrorDetails.success,
    (state, { smartNote, smartNoteId }): KanbanBoardState => {
      const smartNotes = state.quickViewDashboardData.smartNotes.map((smartNoteEntry) => {
        if (smartNoteEntry.id === smartNoteId) {
          return { ...smartNoteEntry, faultCodes: smartNote.faultCodes, loading: false };
        } else {
          return smartNoteEntry;
        }
      });

      return {
        ...state,
        quickViewDashboardData: {
          ...state.quickViewDashboardData,
          smartNotes,
        },
      };
    }
  ),

  on(
    KanbanBoardActions.getQuickViewErrorDetails.error,
    (state, { error, smartNoteId }): KanbanBoardState => {
      const smartNotes = state.quickViewDashboardData.smartNotes.map((smartNoteEntry) => {
        if (smartNoteEntry.id === smartNoteId) {
          return { ...smartNoteEntry, error, loading: false };
        } else {
          return smartNoteEntry;
        }
      });

      return {
        ...state,
        quickViewDashboardData: {
          ...state.quickViewDashboardData,
          smartNotes,
        },
      };
    }
  ),

  on(
    KanbanBoardActions.changeQuickViewDeviceIndex,
    (state, { deviceIndex }): KanbanBoardState => ({
      ...state,
      quickViewDashboardData: {
        ...state.quickViewDashboardData,
        quickViewDeviceIndex: deviceIndex,
      },
    })
  ),

  on(
    KanbanBoardActions.fetchInstallationsForColumn.attempt,
    (state, { payload }): KanbanBoardState => ({
      ...state,
      columns: state.columns.map(
        (column): DashboardInstallationColumn =>
          column.id === payload.columnId ? { ...column, loading: true } : column
      ),
    })
  ),

  on(
    KanbanBoardActions.fetchInstallationsForColumn.success,
    (state, { columnId, installations }): KanbanBoardState => {
      const columns = state.columns.map(
        (column): DashboardInstallationColumn => {
          if (column.id === columnId) {
            const combinedInstallations = column.installations.concat(
              removeEmptyDevices(installations)
            );

            return {
              ...column,
              installations: combinedInstallations,
              loading: false,
              canFetch: column.installationIds.length > combinedInstallations.length,
            };
          }

          return column;
        }
      );

      return {
        ...state,
        columns,
      };
    }
  ),

  on(
    KanbanBoardActions.fetchInstallationsForColumn.error,
    (state, { columnId }): KanbanBoardState => ({
      ...state,
      columns: state.columns.map(
        (column): DashboardInstallationColumn =>
          column.id === columnId ? { ...column, loading: false } : column
      ),
    })
  ),

  on(
    KanbanBoardActions.copyKanbanInstallation.attempt,
    (state, { installation, nextColumnId }): KanbanBoardState => {
      const columns: DashboardInstallationColumn[] = structuredClone(state.columns);
      const columnToEdit = columns.find((column) => column.id === nextColumnId);
      if (!columnToEdit.allInstallationIds.includes(installation.id)) {
        columnToEdit.allInstallationIds.unshift(installation.id);
      }
      columnToEdit.installationIds = columnToEdit.allInstallationIds;
      columnToEdit.installations.unshift(installation);

      return { ...state, columns };
    }
  ),

  on(
    KanbanBoardActions.copyKanbanInstallation.success,
    KanbanBoardActions.moveKanbanColumn.success,
    (state, { columnId, version }): KanbanBoardState => {
      const columns: DashboardInstallationColumn[] = structuredClone(state.columns);
      const columnToEdit = columns.find((column) => column.id === columnId);
      columnToEdit.version = version;

      return { ...state, columns };
    }
  ),

  on(
    KanbanBoardActions.copyKanbanInstallation.error,
    (state, { installation, nextColumnId }): KanbanBoardState => {
      const columns: DashboardInstallationColumn[] = structuredClone(state.columns);
      const columnToEdit = columns.find((column) => column.id === nextColumnId);

      columnToEdit.installations = columnToEdit.installations.filter(
        (el) => el.id !== installation.id
      );
      columnToEdit.allInstallationIds = columnToEdit.allInstallationIds.filter(
        (id) => id !== installation.id
      );
      columnToEdit.installationIds = columnToEdit.allInstallationIds;

      return { ...state, columns };
    }
  ),

  on(
    KanbanBoardActions.moveKanbanInstallation.attempt,
    (state, { installation, nextColumnId, previousColumnId }): KanbanBoardState => {
      const columns: DashboardInstallationColumn[] = structuredClone(state.columns);
      const previousColumn = columns.find((column) => column.id === previousColumnId);
      previousColumn.installations = previousColumn.installations.filter(
        (el) => el.id !== installation.id
      );
      previousColumn.allInstallationIds = previousColumn.allInstallationIds.filter(
        (id) => id !== installation.id
      );
      previousColumn.installationIds = previousColumn.allInstallationIds;

      const nextColumn = columns.find((column) => column.id === nextColumnId);
      nextColumn.installations.unshift(installation);
      nextColumn.allInstallationIds.unshift(installation.id);
      nextColumn.installationIds = nextColumn.allInstallationIds;

      const recoveryDeletionData = state.columns.find((column) => column.id === previousColumnId);

      return { ...state, columns, recoverDataInCaseDeleteError: recoveryDeletionData };
    }
  ),

  on(
    KanbanBoardActions.moveKanbanInstallation.error,
    (state, { installation, previousColumnId, nextColumnId }): KanbanBoardState => {
      const columns: DashboardInstallationColumn[] = structuredClone(state.columns);
      const previousColumnIndex = columns.findIndex((column) => column.id === previousColumnId);
      columns[previousColumnIndex] = state.recoverDataInCaseDeleteError;

      const nextColumn = columns.find((column) => column.id === nextColumnId);
      nextColumn.installations = nextColumn.installations.filter((el) => el.id !== installation.id);
      nextColumn.allInstallationIds = nextColumn.allInstallationIds.filter(
        (id) => id !== installation.id
      );
      nextColumn.installationIds = nextColumn.allInstallationIds.filter(
        (id) => id !== installation.id
      );

      return { ...state, columns, recoverDataInCaseDeleteError: null };
    }
  ),

  on(
    KanbanBoardActions.moveKanbanInstallation.success,
    (state, { columnsToUpdate }): KanbanBoardState => {
      const columns: DashboardInstallationColumn[] = structuredClone(state.columns);
      columnsToUpdate.forEach((column) => {
        const columnToUpdate = columns.find((el) => el.id === column.id);
        columnToUpdate.version = column.version;
      });

      return { ...state, columns, recoverDataInCaseDeleteError: null };
    }
  ),

  on(
    KanbanBoardActions.moveKanbanColumn.attempt,
    (state, { movedColumnIndex, targetColumnIndex }): KanbanBoardState => {
      const columns: DashboardInstallationColumn[] = structuredClone(state.columns);
      const rearrangedColumns = rearrangeColumnOrder(columns, movedColumnIndex, targetColumnIndex);

      return { ...state, columns: rearrangedColumns };
    }
  ),

  on(
    KanbanBoardActions.moveKanbanColumn.error,
    (state, { movedColumnIndex, targetColumnIndex }): KanbanBoardState => {
      const columns: DashboardInstallationColumn[] = structuredClone(state.columns);
      const rearrangedColumns = rearrangeColumnOrder(columns, targetColumnIndex, movedColumnIndex);

      return { ...state, columns: rearrangedColumns };
    }
  ),

  on(
    KanbanBoardActions.updateColumnsAndRefetchInstallations.attempt,
    (state): KanbanBoardState => {
      return {
        ...state,
        forcedStoreUpdateStatus: {
          loading: true,
          loaded: false,
          error: false,
        },
      };
    }
  ),

  on(
    KanbanBoardActions.updateColumnsAndRefetchInstallations.error,
    (state): KanbanBoardState => {
      return {
        ...state,
        forcedStoreUpdateStatus: {
          loading: false,
          loaded: false,
          error: true,
        },
      };
    }
  ),

  on(
    KanbanBoardActions.updateColumnsAndRefetchInstallations.success,
    (state, { dashboard, columnsWithNewVersion }): KanbanBoardState => {
      const storeColumns = structuredClone(state.columns);
      const updatedColumns: DashboardInstallationColumn[] = [];

      dashboard.forEach((column) => {
        const updatedColumn = addAdditionalFlagsToColumn(column);
        const alreadyExistingColumn = storeColumns.find(
          (storeColumn) => storeColumn.id === column.id
        );
        const matchingColumnWithNewVersion = columnsWithNewVersion.find(
          (col) => col.id === column.id
        );

        // No need to change anything
        if (alreadyExistingColumn && !matchingColumnWithNewVersion) {
          updatedColumn.installations = alreadyExistingColumn.installations;
          // Column was changed in meantime
        } else if (alreadyExistingColumn && matchingColumnWithNewVersion) {
          updatedColumn.installations = matchingColumnWithNewVersion.installations;
        }
        // Completely new column
        updatedColumns.push(updatedColumn);
      });

      return {
        ...state,
        columns: updatedColumns,
        forcedStoreUpdateStatus: {
          loading: false,
          loaded: true,
          error: false,
        },
      };
    }
  ),

  on(
    KanbanBoardActions.updateColumnVersions.success,
    (state, { columns }): KanbanBoardState => {
      const storeColumns = structuredClone(state.columns);
      columns.forEach((column) => {
        const columnToEdit = storeColumns.find((storeColumn) => storeColumn.id === column.id);
        columnToEdit.version = column.version;
      });

      return {
        ...state,
        columns: storeColumns,
      };
    }
  ),

  on(
    KanbanBoardActions.deleteKanbanInstallationCopy.attempt,
    (state, { columnId, installationId }): KanbanBoardState => {
      const storeColumns = structuredClone(state.columns);
      const columnToEdit = storeColumns.find((column) => column.id === columnId);
      columnToEdit.installations = columnToEdit.installations.filter(
        (installation) => installation.id !== installationId
      );
      columnToEdit.allInstallationIds = columnToEdit.allInstallationIds.filter(
        (id) => id !== installationId
      );
      columnToEdit.installationIds = columnToEdit.allInstallationIds;

      const columnDataRecovery = state.columns.find((column) => column.id === columnId);

      return {
        ...state,
        columns: storeColumns,
        recoverDataInCaseDeleteError: columnDataRecovery,
      };
    }
  ),

  on(
    KanbanBoardActions.deleteKanbanInstallationCopy.success,
    (state, { columnId, version }): KanbanBoardState => {
      const columns: DashboardInstallationColumn[] = state.columns.map((column) => ({ ...column }));
      const columnToEdit = columns.find((column) => column.id === columnId);

      if (columnToEdit) {
        columnToEdit.version = version;
      }

      return { ...state, columns, recoverDataInCaseDeleteError: null };
    }
  ),

  on(
    KanbanBoardActions.deleteKanbanInstallationCopy.error,
    (state, { columnId }): KanbanBoardState => {
      const storeColumns = structuredClone(state.columns);
      const columnToEditIndex = storeColumns.findIndex((column) => column.id === columnId);
      storeColumns[columnToEditIndex] = state.recoverDataInCaseDeleteError;

      return {
        ...state,
        columns: storeColumns,
        recoverDataInCaseDeleteError: null,
      };
    }
  ),

  on(
    KanbanBoardActions.createNewColumnAndMoveInstallationToIt.createInput,
    (state, { installation, previousColumnId }): KanbanBoardState => {
      const lastColumn = state.columns[state.columns.length - 1];
      const tempColumnToAdd: DashboardInstallationColumn = {
        id: lastColumn.id + 1,
        name: '',
        selfDefined: true,
        sortOrder: SortOrder.asc,
        installations: [installation],
        leftNeighbor: lastColumn.id,
        version: '',
        loading: false,
        canFetch: false,
        isTemporary: true,
        inputShowed: true,
        installationIds: [installation.id],
        allInstallationIds: [installation.id],
      };

      return {
        ...state,
        columns: [...state.columns, tempColumnToAdd],
        idOfOriginalColumnDuringColumnCreationAndMoveProcess: previousColumnId,
        columnCreationFlow: ColumnCreationFlow.AddAndMove,
      };
    }
  ),

  on(
    KanbanBoardActions.createNewColumnAndMoveInstallationToIt.aborted,
    (state): KanbanBoardState => {
      return {
        ...state,
        columnCreationFlow: null,
        idOfOriginalColumnDuringColumnCreationAndMoveProcess: null,
      };
    }
  ),

  on(
    KanbanBoardActions.createNewColumnAndMoveInstallationToIt.columnCreationSuccess,
    (state, { column, installation }): KanbanBoardState => {
      const newColumn: DashboardInstallationColumn = addAdditionalFlagsToColumn({ ...column });
      newColumn.installations = [installation];
      newColumn.allInstallationIds = [installation.id];
      newColumn.installationIds = newColumn.allInstallationIds;
      const columns = state.columns.filter((el) => !el.isTemporary);

      return {
        ...state,
        columns: [...columns, newColumn],
        columnCreationFlow: null,
      };
    }
  ),

  on(
    KanbanBoardActions.createNewColumnAndMoveInstallationToIt.columnCreationFailed,
    (state): KanbanBoardState => {
      return {
        ...state,
        columns: state.columns.filter((column) => !column.isTemporary),
        columnCreationFlow: null,
        idOfOriginalColumnDuringColumnCreationAndMoveProcess: null,
      };
    }
  ),

  on(
    KanbanBoardActions.createNewColumnAndMoveInstallationToIt.moveInstallationAttempt,
    (state, { installation, previousColumnId }): KanbanBoardState => {
      const columns = structuredClone(state.columns);
      const recoveryData = state.columns.find((column) => column.id === previousColumnId);
      const previousColumn = columns.find((column) => column.id === previousColumnId);
      previousColumn.installations = previousColumn.installations.filter(
        (el) => el.id !== installation.id
      );
      previousColumn.allInstallationIds = previousColumn.allInstallationIds.filter(
        (id) => id !== installation.id
      );
      previousColumn.installationIds = previousColumn.allInstallationIds;

      return {
        ...state,
        columns,
        recoverDataInCaseDeleteError: recoveryData,
      };
    }
  ),

  on(
    KanbanBoardActions.createNewColumnAndMoveInstallationToIt.moveInstallationSuccessful,
    (state, { columnsToUpdate }): KanbanBoardState => {
      const columns = structuredClone(state.columns);
      columnsToUpdate.forEach((column) => {
        const columnToUpdate = columns.find((el) => el.id === column.id);
        columnToUpdate.version = column.version;
      });

      return {
        ...state,
        columns,
        recoverDataInCaseDeleteError: null,
        idOfOriginalColumnDuringColumnCreationAndMoveProcess: null,
      };
    }
  ),

  on(
    KanbanBoardActions.createNewColumnAndMoveInstallationToIt.moveInstallationFailed,
    (state, { installation, nextColumnId, previousColumnId }): KanbanBoardState => {
      const columns = structuredClone(state.columns);
      const previousColumnIndex = columns.findIndex((column) => column.id === previousColumnId);
      columns[previousColumnIndex] = state.recoverDataInCaseDeleteError;

      const nextColumn = columns.find((column) => column.id === nextColumnId);
      nextColumn.installations = nextColumn.installations.filter((el) => el.id !== installation.id);
      nextColumn.allInstallationIds = nextColumn.allInstallationIds.filter(
        (id) => id !== installation.id
      );
      nextColumn.installationIds = nextColumn.allInstallationIds;

      return {
        ...state,
        columns,
        recoverDataInCaseDeleteError: null,
        idOfOriginalColumnDuringColumnCreationAndMoveProcess: null,
      };
    }
  ),

  on(
    KanbanBoardActions.createNewColumnAndCopyInstallationToIt.createInput,
    (state, { installation }): KanbanBoardState => {
      const lastColumn = state.columns[state.columns.length - 1];
      const tempColumnToAdd: DashboardInstallationColumn = {
        id: lastColumn.id + 1,
        name: '',
        selfDefined: true,
        sortOrder: SortOrder.asc,
        installations: [installation],
        leftNeighbor: lastColumn.id,
        version: '',
        loading: false,
        canFetch: false,
        isTemporary: true,
        inputShowed: true,
        installationIds: [installation.id],
        allInstallationIds: [installation.id],
      };

      return {
        ...state,
        columns: [...state.columns, tempColumnToAdd],
        columnCreationFlow: ColumnCreationFlow.AddAndCopy,
      };
    }
  ),

  on(
    KanbanBoardActions.createNewColumnAndCopyInstallationToIt.aborted,
    (state): KanbanBoardState => {
      const columns = state.columns.filter((el) => !el.isTemporary);

      return {
        ...state,
        columns,
        columnCreationFlow: null,
      };
    }
  ),

  on(
    KanbanBoardActions.createNewColumnAndCopyInstallationToIt.columnCreationSuccess,
    (state, { column, installation }): KanbanBoardState => {
      column.installations = [installation];
      const newColumn: DashboardInstallationColumn = addAdditionalFlagsToColumn({ ...column });
      const columns = state.columns.filter((el) => !el.isTemporary);

      return {
        ...state,
        columns: [...columns, newColumn],
        columnCreationFlow: null,
      };
    }
  ),

  on(
    KanbanBoardActions.createNewColumnAndCopyInstallationToIt.columnCreationFailed,
    (state): KanbanBoardState => {
      const columns = state.columns.filter((el) => !el.isTemporary);

      return {
        ...state,
        columns,
        columnCreationFlow: null,
      };
    }
  ),

  on(
    KanbanBoardActions.initialFetchInstallationList.attempt,
    (state): KanbanBoardState => {
      return {
        ...state,
        installationList: {
          ...state.installationList,
          loading: true,
          value: null,
          canFetch: false,
        },
      };
    }
  ),

  on(
    KanbanBoardActions.initialFetchInstallationList.success,
    KanbanBoardActions.retryInitialFetchInstallationList.success,
    (state, { installations, cursor }): KanbanBoardState => {
      return {
        ...state,
        installationList: {
          ...state.installationList,
          value: removeEmptyDevices(installations),
          loading: false,
          canFetch: !!cursor,
          cursor,
        },
      };
    }
  ),

  on(
    KanbanBoardActions.initialFetchInstallationList.error,
    KanbanBoardActions.retryInitialFetchInstallationList.error,
    (state, { error }): KanbanBoardState => {
      return {
        ...state,
        installationList: {
          ...state.installationList,
          loading: false,
          error,
        },
      };
    }
  ),

  on(
    KanbanBoardActions.nextFetchInstallationList.attempt,
    (state): KanbanBoardState => {
      return {
        ...state,
        installationList: {
          ...state.installationList,
          loading: true,
          canFetch: false,
        },
      };
    }
  ),

  on(
    KanbanBoardActions.nextFetchInstallationList.success,
    KanbanBoardActions.retryNextFetchInstallationList.success,
    (state, { installations, cursor }): KanbanBoardState => {
      const updatedInstallationList = state.installationList.value.concat(
        removeEmptyDevices(installations)
      );

      return {
        ...state,
        installationList: {
          ...state.installationList,
          value: updatedInstallationList,
          loading: false,
          canFetch: !!cursor,
          cursor,
        },
      };
    }
  ),

  on(
    KanbanBoardActions.nextFetchInstallationList.error,
    KanbanBoardActions.retryNextFetchInstallationList.error,
    (state, { error }): KanbanBoardState => {
      return {
        ...state,
        installationList: {
          ...state.installationList,
          loading: false,
          error,
        },
      };
    }
  ),

  on(
    KanbanBoardActions.clearSelectedFilters,
    (state): KanbanBoardState => ({
      ...state,
      selectedFilters: [],
    })
  ),

  on(
    KanbanBoardActions.getButtonFilterData.success,
    (state, { buttonFilterData }): KanbanBoardState => {
      return {
        ...state,
        installationList: {
          ...state.installationList,
          buttonFilterData,
        },
      };
    }
  ),

  on(
    KanbanBoardActions.applyFiltersByClickingButton,
    (state, { selectedFilter }): KanbanBoardState => {
      let selectedFilters = [...state.selectedFilters];

      let filtersToCheck: DashboardFilter[] = [];

      if (Array.isArray(selectedFilter.filterValue)) {
        selectedFilter.filterValue.forEach((filter) => {
          const filterToCheck = state.filters.find(
            (stateFilter) =>
              stateFilter.filterName === selectedFilter.filterName && stateFilter.value === filter
          );
          if (filterToCheck) filtersToCheck.push(filterToCheck);
        });
      } else {
        filtersToCheck = [
          state.filters.find((filter) => filter.filterName === selectedFilter.filterName),
        ];
      }

      const isEveryFilterPresent = filtersToCheck.every((filterToCheck) =>
        selectedFilters.some((selectedFilter) => isSameFilter(selectedFilter, filterToCheck))
      );

      if (isEveryFilterPresent) {
        // Unselect every filter
        selectedFilters = selectedFilters.filter(
          (selectedFilter) =>
            !filtersToCheck.some((anomalyFilter) => isSameFilter(selectedFilter, anomalyFilter))
        );
      } else {
        // Select all filters
        selectedFilters = selectedFilters.concat(filtersToCheck);
        selectedFilters = uniqWith(selectedFilters, isEqual);
      }

      return {
        ...state,
        selectedFilters,
      };
    }
  ),

  on(
    KanbanBoardActions.refreshSnapshotInInstallationList.attempt,
    (state): KanbanBoardState => {
      return {
        ...state,
        installationList: {
          buttonFilterData: null,
          canFetch: false,
          error: null,
          loading: true,
          value: null,
          cursor: null,
        },
      };
    }
  )
);

export function sortColumnsByNeighbor(
  inputArray: DashboardColumn[],
  outputArray: DashboardColumn[] = []
): DashboardColumn[] {
  if (!inputArray.length) {
    return outputArray;
  }

  let nextElementIndex: number;
  if (!outputArray.length) {
    nextElementIndex = inputArray.findIndex((column) => column.leftNeighbor === null);
  } else {
    const lastElementId = outputArray[outputArray.length - 1].id;
    nextElementIndex = inputArray.findIndex((column) => column.leftNeighbor === lastElementId);
  }

  const columnToAdd = inputArray.splice(nextElementIndex, 1);
  outputArray = [...outputArray, ...columnToAdd];

  return sortColumnsByNeighbor(inputArray, outputArray);
}

export function addAdditionalFlagsToColumn(column: DashboardColumn): DashboardInstallationColumn {
  return {
    ...column,
    installationIds: column.installationIds ? column.installationIds : [], // Adding column scenario
    allInstallationIds: column.installationIds ? column.installationIds : [], // Adding column scenario
    canFetch: column.installationIds?.length > column.installations?.length, // Adding column scenario
    loading: false,
  };
}

function rearrangeColumnOrder(
  columns: DashboardInstallationColumn[],
  movedColumnIndex: number,
  targetColumnIndex: number
): DashboardInstallationColumn[] {
  moveItemInArray(columns, movedColumnIndex, targetColumnIndex);

  return columns.map((el, index) => {
    if (index === 0) {
      return { ...el, leftNeighbor: null };
    } else {
      return { ...el, leftNeighbor: columns[index - 1].id };
    }
  });
}

function isSameFilter(filter1: DashboardFilter, filter2: DashboardFilter): boolean {
  return filter1.value === filter2.value && filter1.filterName === filter2.filterName;
}

function removeEmptyDevices(
  installations: DashboardInstallationData[]
): DashboardInstallationData[] {
  return installations.map((installation) => ({
    ...installation,
    devices: installation.devices.filter((device) => {
      const noRoles = !device.roles?.length;
      const unwantedRoles = [DeviceRoles.Virtual, DeviceRoles.Gateway];
      const isOnlyUnwatedRole =
        device.roles?.length === 1 && unwantedRoles?.includes(device.roles[0]);
      const isVirtualIncluded = device.roles?.includes(DeviceRoles.Virtual);

      if (noRoles || isOnlyUnwatedRole || isVirtualIncluded) {
        return false;
      }

      return true;
    }),
  }));
}
